import { useState, useEffect } from "react";
import { Star, Loader2, Shield, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/api/client";
import { logger } from "@/utils/logger";

const StarRating = ({ rating, size = "w-4 h-4", interactive = false, onChange }) => {
  const [hovered, setHovered] = useState(0);
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map(i => (
        <button
          key={i}
          type="button"
          disabled={!interactive}
          onClick={() => interactive && onChange?.(i)}
          onMouseEnter={() => interactive && setHovered(i)}
          onMouseLeave={() => interactive && setHovered(0)}
          className={interactive ? "cursor-pointer" : "cursor-default"}
        >
          <Star
            className={`${size} ${
              i <= (hovered || rating) ? "fill-[#D4AF37] text-[#D4AF37]" : "text-[#3F3F46]"
            } transition-colors`}
          />
        </button>
      ))}
    </div>
  );
};

const ReviewSection = ({ targetType, targetId, user }) => {
  const [reviews, setReviews] = useState([]);
  const [avgRating, setAvgRating] = useState(0);
  const [reviewCount, setReviewCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ rating: 0, title: "", body: "" });

  const fetchReviews = async () => {
    try {
      const res = await api.get(`/reviews/${targetType}/${targetId}`);
      setReviews(res.data.reviews || []);
      setAvgRating(res.data.avg_rating);
      setReviewCount(res.data.review_count);
    } catch (err) { logger.error(err); } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchReviews(); }, [targetType, targetId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.rating === 0) { toast.error("Please select a rating"); return; }
    setSubmitting(true);
    try {
      await api.post(`/reviews/${targetType}/${targetId}`, form);
      toast.success("Review submitted!");
      setShowForm(false);
      setForm({ rating: 0, title: "", body: "" });
      fetchReviews();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit review");
    } finally {
      setSubmitting(false);
    }
  };

  const visibleReviews = showAll ? reviews : reviews.slice(0, 3);

  return (
    <div data-testid="review-section">
      {/* Summary */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <StarRating rating={Math.round(avgRating)} />
          <span className="text-[#EDEDED] text-sm font-medium">{avgRating > 0 ? avgRating : "No"} {avgRating > 0 ? "" : "ratings"}</span>
          <span className="text-[#71717A] text-sm">({reviewCount} review{reviewCount !== 1 ? "s" : ""})</span>
        </div>
        {user && (
          <button
            onClick={() => setShowForm(!showForm)}
            className="text-sm text-[#D4AF37] hover:underline"
            data-testid="write-review-btn"
          >
            Write a review
          </button>
        )}
      </div>

      {/* Review Form */}
      {showForm && (
        <form onSubmit={handleSubmit} className="glass-card p-4 mb-4 space-y-3" data-testid="review-form">
          <div>
            <label className="block text-sm text-[#A1A1AA] mb-1">Rating *</label>
            <StarRating rating={form.rating} size="w-6 h-6" interactive onChange={(r) => setForm(f => ({ ...f, rating: r }))} />
          </div>
          <input
            value={form.title}
            onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))}
            placeholder="Review title (optional)"
            className="input-minimal w-full"
          />
          <textarea
            value={form.body}
            onChange={(e) => setForm(f => ({ ...f, body: e.target.value }))}
            placeholder="Share your experience..."
            className="input-minimal w-full min-h-[80px]"
          />
          <div className="flex gap-2">
            <button type="submit" disabled={submitting} className="btn-gold text-sm px-4 py-2 disabled:opacity-50" data-testid="submit-review-btn">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Submit"}
            </button>
            <button type="button" onClick={() => setShowForm(false)} className="btn-secondary text-sm px-4 py-2">Cancel</button>
          </div>
        </form>
      )}

      {/* Reviews List */}
      {loading ? (
        <div className="flex justify-center py-4"><Loader2 className="w-5 h-5 animate-spin text-[#D4AF37]" /></div>
      ) : reviews.length === 0 ? (
        <p className="text-[#71717A] text-sm py-4">No reviews yet. Be the first to review!</p>
      ) : (
        <div className="space-y-3">
          {visibleReviews.map(r => (
            <div key={r.review_id} className="p-4 bg-[#0A0A0A] border border-white/5 rounded-lg" data-testid={`review-${r.review_id}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <StarRating rating={r.rating} size="w-3.5 h-3.5" />
                  {r.is_verified_client && (
                    <span className="flex items-center gap-1 text-xs text-[#22C55E]">
                      <Shield className="w-3 h-3" /> Verified Client
                    </span>
                  )}
                </div>
                <span className="text-xs text-[#52525B]">{new Date(r.created_at).toLocaleDateString()}</span>
              </div>
              {r.title && <p className="text-sm text-[#EDEDED] font-medium mb-1">{r.title}</p>}
              {r.body && <p className="text-sm text-[#A1A1AA]">{r.body}</p>}
              <p className="text-xs text-[#52525B] mt-2">{r.reviewer_name || "Anonymous"}</p>
            </div>
          ))}
          {reviews.length > 3 && (
            <button
              onClick={() => setShowAll(!showAll)}
              className="text-sm text-[#D4AF37] hover:underline flex items-center gap-1"
            >
              {showAll ? <><ChevronUp className="w-4 h-4" /> Show less</> : <><ChevronDown className="w-4 h-4" /> Show all {reviews.length} reviews</>}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export { StarRating };
export default ReviewSection;
